// @flow
import logStats from 'app/common/lib/logStats';

const APP_SHELL_FILE = '/desktop-sw-shell.html';
const SAMPLE_RATE = 0.1;

type Subscription = {
  subscriptionId?: string,
  endpoint: string,
  unsubscribe: () => Promise<boolean>,
};

type ServiceWorkerRegistration = {
  onupdatefound: () => void,
  update: () => Promise<empty>,
  unregister: () => Promise<empty>,
};

type ServiceWorker = {
  ready: Promise<ServiceWorker>,
  register: (string) => Promise<ServiceWorkerRegistration>,
  getRegistration: () => Promise<ServiceWorkerRegistration>,
  pushManager: {
    subscribe: ({ userVisibleOnly: true }) => Promise<Subscription>,
    getSubscription: () => Promise<Subscription>,
  },
};

export const hasActiveServiceWorker = () =>
  window && !!window.navigator.serviceWorker && !!window.navigator.serviceWorker.controller;

export const isAppShell = (initialPageContext: Object) =>
  initialPageContext && initialPageContext.appShell && hasActiveServiceWorker();

export const clearAppShell = (source?: string): Promise<?any> => {
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  const sw: ?ServiceWorker = navigator.serviceWorker;
  logStats.increment('web_service_worker.clear_app_shell', SAMPLE_RATE, {
    source: source || 'unknown',
  });
  return sw && window.caches
    ? window.caches.keys().then((cacheKeys) => {
        const precacheKey = cacheKeys.find((key) => key.startsWith('workbox-precaching'));
        if (precacheKey) {
          logStats.increment('web_service_worker.clear_app_shell.precache.exist', SAMPLE_RATE, {
            source: source || 'unknown',
          });
          window.caches.open(precacheKey).then((cache) => {
            cache.delete(APP_SHELL_FILE);
            logStats.increment('web_service_worker.clear_app_shell.delete', SAMPLE_RATE, {
              source: source || 'unknown',
            });
          });
        } else {
          logStats.increment('web_service_worker.clear_app_shell.precache.not_exist', SAMPLE_RATE, {
            source,
          });
          Promise.resolve();
        }
      })
    : Promise.resolve();
};

export const installServiceWorker = (): Promise<?any> =>
  new Promise((resolve, reject) => {
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    const sw: ?ServiceWorker = navigator.serviceWorker;
    logStats.increment('web_service_worker.install', SAMPLE_RATE);
    if (sw && sw.register) {
      sw.register('/sw.js')
        .then((reg) => {
          logStats.increment('web_service_worker.install.success', SAMPLE_RATE);
          resolve(reg);
        })
        .catch((e) => {
          logStats.increment('web_service_worker.install.error', SAMPLE_RATE);
          reject(e);
        });
    } else {
      resolve();
    }
  });

export const updateServiceWorker = (): Promise<?any> =>
  new Promise((resolve, reject) => {
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    const sw: ?ServiceWorker = navigator.serviceWorker;
    logStats.increment('web_service_worker.update', SAMPLE_RATE);
    if (sw && sw.getRegistration) {
      sw.getRegistration().then((reg) => {
        if (reg) {
          logStats.increment('web_service_worker.update.registration.exist', SAMPLE_RATE);
          reg
            .update()
            .then(() => {
              logStats.increment('web_service_worker.update.success', SAMPLE_RATE);
              resolve(reg);
            })
            .catch((e) => {
              logStats.increment('web_service_worker.update.fail', SAMPLE_RATE);
              reject(e);
            });
        } else {
          logStats.increment('web_service_worker.update.registration.not_exist', SAMPLE_RATE);
          installServiceWorker().then(resolve);
        }
      });
    } else {
      resolve();
    }
  });

export const unregisterServiceWorker = (source?: string): Promise<?any> => {
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  const sw: ?ServiceWorker = navigator.serviceWorker;
  logStats.increment('web_service_worker.unregister', SAMPLE_RATE, { source: source || 'unknown' });
  if (sw && sw.getRegistration) {
    return sw
      .getRegistration()
      .then((reg) => {
        if (reg) {
          logStats.increment('web_service_worker.unregister.registration.exist', SAMPLE_RATE, {
            source: source || 'unknown',
          });
          // unregister sw and clear app shell
          return reg.unregister().then(() => {
            logStats.increment('web_service_worker.unregister.success', SAMPLE_RATE, {
              source: source || 'unknown',
            });
            clearAppShell(source);
          });
        } else {
          logStats.increment('web_service_worker.unregister.registration.not_exist', SAMPLE_RATE, {
            source: source || 'unknown',
          });
          return Promise.resolve();
        }
      })
      .catch(() => {
        logStats.increment('web_service_worker.unregister.fail', SAMPLE_RATE, {
          source: source || 'unknown',
        });
      });
  }
  return Promise.resolve();
};

export default undefined;
